import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ActorResolverProvider } from '~/components/common/actor-resolver-provider';
import { AnalyticsSyncManager } from '~/features/analytics-sync/components/analytics-sync-manager';
import { ExportProgressManager } from '~/features/exports/components/export-progress-manager';
import { NotificationsProvider } from '~/features/notifications/components/notifications-provider';
import { CatalogImportProgressManager } from '~/features/onboarding/components/catalog-import-progress-manager';
import { useTrialSync } from '~/hooks/use-trial-sync';
import { useEnrolledProducts } from '~/hooks/useEnrolledProducts';
import { consumeSubsidiaryDenied, reconcileSubsidiary } from '~/utils/guards';

export const Route = createFileRoute('/_authenticated/_tenant')({
  // Reconcile the ?sub=<id> URL param against the active subsidiary (switch on
  // an accessible mismatch, back-fill otherwise). See docs/subsidiary-url-identifier.md.
  beforeLoad: reconcileSubsidiary,
  component: TenantLayout,
});

/**
 * Layout for tenant-aware routes.
 * Tenant context (activeTenantId, roleCode, etc.) is available via auth store.
 * `NotificationsProvider` opens the subsidiary-scoped SSE stream once auth +
 * tenant guards have passed, and tears it down on logout / tenant switch.
 * `useEnrolledProducts` prefetches enrolled products into the TanStack Query
 * cache so all child routes get an instant cache hit — no per-page loading state.
 * `useTrialSync` derives the isTrial flag from enrolled products and writes it
 * to trial.store so all child routes can read it without re-fetching.
 * `AnalyticsSyncManager` watches in-flight analytics-sync sessions and drives
 * their progress toasts + polling, mounted here so both survive navigation.
 */
function TenantLayout() {
  const { t } = useTranslation();
  useEnrolledProducts();
  useTrialSync();

  // Fire the "no access" toast exactly once when the reconcile guard rejected a
  // shared ?sub link the user can't reach. Marker-based (not URL) so a refresh
  // or re-share of the landed URL never re-triggers it.
  useEffect(() => {
    if (consumeSubsidiaryDenied()) {
      toast.error(t('subsidiary.noAccess'));
    }
  }, [t]);

  return (
    <>
      <NotificationsProvider />
      <AnalyticsSyncManager />
      <ExportProgressManager />
      <CatalogImportProgressManager />
      {/* STOPGAP: client-side fallback that names actors the API returned as
          unresolved short ids. Mounted here so every actor surface resolves
          identically; remove once server-side resolution is reliable. */}
      <ActorResolverProvider>
        <Outlet />
      </ActorResolverProvider>
    </>
  );
}
